import { FC, useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ThumbsUpIcon,
  ThumbsDownIcon,
  ReplyIcon,
  Trash2Icon,
  PencilIcon,
  Loader2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { useAuthToken } from '@/hooks/useAuthToken';
import { deleteComment, reactToComment, updateComment } from '@/api/comments';
import { Textarea } from '@/components/ui/textarea';
import { NbButton } from '@/components/Neobrut';
import { CommentForm } from './CommentForm';

interface CommentItemProps {
  comment: Comment;
  petId: number;
  queryKey: string;
  isReply?: boolean;
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);

  if (diffMin < 1) return 'ahora mismo';
  if (diffMin < 60) return `hace ${diffMin} min`;
  if (diffHrs < 24) return `hace ${diffHrs} h`;
  if (diffDays < 7) return `hace ${diffDays} d`;
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

export const CommentItem: FC<CommentItemProps> = ({ comment, petId, queryKey, isReply = false }) => {
  const navigate = useNavigate();
  const { token, isTokenExpired, userIdFromToken } = useAuthToken();
  const queryClient = useQueryClient();

  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);

  const isOwner = token && !isTokenExpired(token) && userIdFromToken(token) === comment.created_by.id;
  const authorInitials = `${comment.created_by.first_name[0] ?? ''}${comment.created_by.last_name[0] ?? ''}`.toUpperCase();
  const authorName = `${comment.created_by.first_name} ${comment.created_by.last_name}`.trim();

  const requireAuth = useCallback(() => {
    if (!token || isTokenExpired(token)) {
      toast.info('Iniciá sesión para interactuar con los comentarios.');
      navigate('/login');
      return false;
    }
    return true;
  }, [token, isTokenExpired, navigate]);

  const reactMutation = useMutation({
    mutationFn: (reaction: 'like' | 'dislike') => reactToComment(comment.id, reaction, token!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKey] }),
    onError: () => toast.error('No se pudo registrar tu reacción.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteComment(comment.id, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Comentario eliminado.');
    },
    onError: () => toast.error('No se pudo eliminar el comentario.'),
  });

  const editMutation = useMutation({
    mutationFn: () => updateComment(comment.id, editContent, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setIsEditing(false);
      toast.success('Comentario actualizado.');
    },
    onError: () => toast.error('No se pudo actualizar el comentario.'),
  });

  const handleReact = (reaction: 'like' | 'dislike') => {
    if (!requireAuth()) return;
    reactMutation.mutate(reaction);
  };

  const handleDelete = () => {
    if (!requireAuth()) return;
    deleteMutation.mutate();
  };

  const handleEditSave = () => {
    if (!editContent.trim()) return;
    editMutation.mutate();
  };

  return (
    <div className={`flex flex-col gap-2 ${isReply ? 'ml-6 border-l-2 border-[var(--nb-ink)] pl-3' : ''}`}>
      <div
        className="p-3"
        style={{
          border: '2px solid var(--nb-ink)',
          background: 'var(--nb-paper)',
          boxShadow: '3px 3px 0 var(--nb-ink)',
          borderRadius: 'var(--nb-radius)',
        }}
      >
        {/* Header */}
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="fill-found flex h-8 w-8 items-center justify-center text-[11px] font-bold"
              style={{
                border: '2px solid var(--nb-ink)',
                borderRadius: 'var(--nb-radius)',
                fontFamily: '"Archivo Black", sans-serif',
              }}
            >
              {authorInitials || '?'}
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-bold leading-tight">
                {authorName || comment.created_by.email}
              </span>
              <span
                className="text-[10px] opacity-60"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                {formatRelativeDate(comment.created_at)}
              </span>
            </div>
          </div>

          {isOwner && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsEditing((v) => !v)}
                aria-label="Editar"
                className="nb-btn nb-btn-sm fill-paper"
                style={{ padding: '4px 5px' }}
              >
                <PencilIcon size={12} />
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                aria-label="Eliminar"
                className="nb-btn nb-btn-sm fill-urgent"
                style={{ padding: '4px 5px' }}
              >
                {deleteMutation.isPending ? (
                  <Loader2Icon size={12} className="animate-spin" />
                ) : (
                  <Trash2Icon size={12} />
                )}
              </button>
            </div>
          )}
        </div>

        {/* Content or Edit Mode */}
        {isEditing ? (
          <div className="flex flex-col gap-2">
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="min-h-[60px] rounded-md border-2 border-[var(--nb-ink)] text-sm focus-visible:ring-0"
              maxLength={500}
            />
            <div className="flex gap-2">
              <NbButton
                size="sm"
                fill="paper"
                onClick={() => setIsEditing(false)}
                disabled={editMutation.isPending}
              >
                Cancelar
              </NbButton>
              <NbButton
                size="sm"
                fill="ink"
                onClick={handleEditSave}
                disabled={editMutation.isPending || !editContent.trim()}
              >
                {editMutation.isPending && <Loader2Icon size={12} className="animate-spin" />}
                Guardar
              </NbButton>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-wrap break-words text-[14px]">{comment.content}</p>
        )}

        {/* Actions */}
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={() => handleReact('like')}
            disabled={reactMutation.isPending}
            className="nb-btn nb-btn-sm"
            style={{
              padding: '3px 8px',
              background:
                comment.user_reaction === 'like' ? 'var(--nb-solved)' : 'var(--nb-paper)',
            }}
          >
            <ThumbsUpIcon size={12} />
            <span className="text-[11px] font-bold">{comment.likes_count}</span>
          </button>

          <button
            onClick={() => handleReact('dislike')}
            disabled={reactMutation.isPending}
            className="nb-btn nb-btn-sm"
            style={{
              padding: '3px 8px',
              background:
                comment.user_reaction === 'dislike' ? 'var(--nb-urgent)' : 'var(--nb-paper)',
            }}
          >
            <ThumbsDownIcon size={12} />
            <span className="text-[11px] font-bold">{comment.dislikes_count}</span>
          </button>

          {!isReply && (
            <button
              onClick={() => {
                if (!requireAuth()) return;
                setShowReplyForm((v) => !v);
              }}
              className="nb-btn nb-btn-sm fill-paper"
              style={{ padding: '3px 8px' }}
            >
              <ReplyIcon size={12} />
              <span className="text-[11px] font-bold">Responder</span>
            </button>
          )}
        </div>
      </div>

      {/* Reply Form */}
      {showReplyForm && !isReply && (
        <div className="ml-6">
          <CommentForm
            petId={petId}
            parentId={comment.id}
            queryKey={queryKey}
            onSuccess={() => {
              setShowReplyForm(false);
              setShowReplies(true);
            }}
            placeholder={`Respondiendo a ${authorName}…`}
            compact
          />
        </div>
      )}

      {/* Replies */}
      {!isReply && comment.replies && comment.replies.length > 0 && (
        <div className="ml-6 flex flex-col gap-2">
          <button
            onClick={() => setShowReplies((v) => !v)}
            className="flex items-center gap-1 self-start text-[11px] font-bold uppercase tracking-[0.04em] underline decoration-2 underline-offset-2"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            {showReplies ? <ChevronUpIcon size={12} /> : <ChevronDownIcon size={12} />}
            {showReplies ? 'Ocultar' : 'Ver'} {comment.replies.length}{' '}
            {comment.replies.length === 1 ? 'respuesta' : 'respuestas'}
          </button>

          {showReplies &&
            comment.replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                petId={petId}
                queryKey={queryKey}
                isReply
              />
            ))}
        </div>
      )}
    </div>
  );
};
